import React from "react";
import { isRentmanReturnedEvent } from "../../Utils/rentmanStatus";

/**
 * Helper function to convert an HSL color string to an HSLA string with a given alpha.
 */
const toHsla = (hsl, alpha) => {
  if (!hsl || typeof hsl !== "string" || !hsl.startsWith("hsl")) {
    // Fallback for non-HSL colors, though Rentman events should use HSL
    return "hsla(0, 0%, 0%, 0.1)";
  }
  return hsl.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
};

/**
 * Neutral “returned” look: same prep / main / retur contrast as HSL events,
 * using rgb(9 9 9 / …) with softer borders.
 */
const RETURNED_SEGMENT = {
  borderOuter: "rgb(9 9 9 / 26%)",
  prepBg: "rgb(9 9 9 / 5%)",
  opptakBg: "rgb(9 9 9 / 12%)",
  returBg: "rgb(9 9 9 / 5%)",
  divider: "rgb(9 9 9 / 12%)",
};

/**
 * [CORRECTED] Calculates the number of days between two dates for an inclusive calendar.
 * This version is more robust and correctly handles date objects from Firestore.
 */
const countDaysInclusive = (start, end) => {
  if (!start || !end) return 0;
  const msPerDay = 1000 * 60 * 60 * 24;
  
  // Create a new Date at the start of the day to avoid side effects
  const startDate = new Date(start); 
  startDate.setUTCHours(0, 0, 0, 0);
  
  const endDate = new Date(end);
  endDate.setUTCHours(0, 0, 0, 0);
  
  const diff = endDate.getTime() - startDate.getTime();
  if (diff < 0) return 0;
  return Math.round(diff / msPerDay) + 1;
};

/**
 * Counts days touched using local time (prevents off-by-one around UTC).
 * Intended for standard (non-Rentman) events.
 */
const countDaysInclusiveLocal = (start, end) => {
  if (!start || !end) return 0;
  const msPerDay = 1000 * 60 * 60 * 24;
  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(0, 0, 0, 0);
  const diff = endDate.getTime() - startDate.getTime();
  if (diff < 0) return 0;
  return Math.round(diff / msPerDay) + 1;
};

/**
 * Safely converts a Firestore Timestamp or legacy timestamp to a Date object
 * Handles both modern Firestore Timestamps (with toDate()) and legacy format (with _seconds)
 */
const safeToDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp._seconds) {
    // Legacy Firestore v11 format
    return new Date(timestamp._seconds * 1000 + Math.floor(timestamp._nanoseconds / 1e6));
  }
  return new Date(timestamp);
};

/** DD.MM.YY in UTC — matches Rentman / opptak day boundaries in this file */
const formatDdMmYyUtc = (dateInput) => {
  const d = dateInput instanceof Date ? dateInput : safeToDate(dateInput);
  if (!d || Number.isNaN(d.getTime())) return "";
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
};

/** Matches Firestore boolean and loose truthy sync copies */
const isVacationEvent = (e) =>
  e && (e.vacation === true || e.vacation === 'true' || e.vacation === 1);

const isSickEvent = (e) =>
  e && (e.sick === true || e.sick === 'true' || e.sick === 1);

const isSickChildEvent = (e) =>
  e && (e.sickChild === true || e.sickChild === 'true' || e.sickChild === 1);

/**
 * [DEFINITIVE VERSION]
 * Creates the custom render function for the Eventcalendar. It renders Rentman
 * events with a proportional layout and includes a safety check for malformed data.
 */
export const CreateMyScheduleEvent = (mobileShow) => {
  return (data) => {
    const ev = data?.original != null ? data.original : data;
    const color = ev.color || data.color;
    const returnedNeutral = ev.rentmanEvent && isRentmanReturnedEvent(ev);
    // Debug logging (can be removed in production)
    // console.log('🗄️ Event Renderer:', ev);

    // --- GROUP SUMMARY LINE (parent header row) --- //
    if (ev.isSummaryLine) {
      const start = safeToDate(ev.start);
      const end = safeToDate(ev.end);
      if (!start || !end) return null;

      const usageStart = ev.usageStart ? safeToDate(ev.usageStart) : null;
      const usageEnd = ev.usageEnd ? safeToDate(ev.usageEnd) : null;
      const hasRentmanSegments = usageStart && usageEnd;

      if (hasRentmanSegments) {
        const total = countDaysInclusive(start, end) || 1;
        const prepDays = countDaysInclusive(
          start,
          new Date(usageStart.getTime() - 1000 * 60 * 60 * 24)
        );
        const opptakDays = countDaysInclusive(usageStart, usageEnd);
        const returStart = new Date(usageEnd.getTime() + 1000 * 60 * 60 * 24);
        const returDays = countDaysInclusive(returStart, end);
        const pct = (n) => `${(n / total) * 100}%`;

        return (
          <div
            className="summary-line"
            style={{ "--line-color": color }}
          >
            {prepDays > 0 && (
              <div
                className="summary-seg summary-seg--check"
                style={{ width: pct(prepDays) }}
              />
            )}
            {opptakDays > 0 && (
              <div
                className="summary-seg summary-seg--main"
                style={{ width: pct(opptakDays) }}
              />
            )}
            {returDays > 0 && (
              <div
                className="summary-seg summary-seg--check"
                style={{ width: pct(returDays) }}
              />
            )}
          </div>
        );
      }

      return (
        <div className="summary-line" style={{ "--line-color": color }}>
          <div className="summary-seg summary-seg--main" style={{ width: "100%" }} />
        </div>
      );
    }

    // --- RENTMAN EVENT LOGIC --- //
    if (ev.rentmanEvent) {
      if (!ev.planStart || !ev.usageStart || !ev.planEnd) {
        return null;
      }

      const planStart = safeToDate(ev.planStart);
      const usageStart = safeToDate(ev.usageStart);
      const usageEnd = safeToDate(ev.usageEnd);
      const planEnd = safeToDate(ev.planEnd);

      const prepDays = countDaysInclusive(
        planStart,
        new Date(usageStart.getTime() - 1)
      );
      const opptakDays = countDaysInclusive(usageStart, usageEnd);
      const returStart = new Date(usageEnd.getTime() + 1000 * 60 * 60 * 24);
      const returDays = countDaysInclusive(returStart, planEnd);

      const totalCalculatedDays = prepDays + opptakDays + returDays;

      const prepPercent =
        totalCalculatedDays > 0 ? (prepDays / totalCalculatedDays) * 100 : 0;
      const opptakPercent =
        totalCalculatedDays > 0 ? (opptakDays / totalCalculatedDays) * 100 : 0;
      const returPercent =
        totalCalculatedDays > 0 ? (returDays / totalCalculatedDays) * 100 : 0;

      const borderColor = returnedNeutral
        ? RETURNED_SEGMENT.borderOuter
        : color;
      const prepFill = returnedNeutral
        ? RETURNED_SEGMENT.prepBg
        : toHsla(color, 0.1);
      const opptakFill = returnedNeutral
        ? RETURNED_SEGMENT.opptakBg
        : toHsla(color, 0.2);
      const returFill = returnedNeutral
        ? RETURNED_SEGMENT.returBg
        : toHsla(color, 0.1);
      const segmentRuleColor = returnedNeutral
        ? RETURNED_SEGMENT.divider
        : color;

      return (
        <div
          className={`mbsc-schedule-event-custom ${
            mobileShow ? "" : "mobile-toggle"
          }`}
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            borderRadius: "5px",
            border: `1px solid ${borderColor}`,
            position: "relative",
            color: "black",
          }}
        >
          {/* This is the sticky title layer, your fix is preserved */}
          <div
            className="mbsc-schedule-event-inner"
            style={{
              padding: "3px 6px",
              fontSize: "12px",
              fontWeight: "bold",
              color: "black",
              whiteSpace: "nowrap",
              zIndex: 2,
              marginLeft: `calc(${prepPercent}% + 5px)`,
              marginRight: `calc(${returPercent}%)`,
              // REMOVED: `overflow: 'hidden'` which was breaking the sticky animation
            }}
          >
            {ev.parentProject.Title}{ev.title === ev.parentProject.Title ? "" :  " - " + ev.title}
            <div className="md-timeline-template-time">
              {formatDdMmYyUtc(usageStart)} - {formatDdMmYyUtc(usageEnd)} | {opptakDays}{" "}
              {opptakDays === 1 ? "day" : "days"}
              {returnedNeutral ? " | Returned" : ""}
            </div>
          </div>

          {/* This container and its children are for the background visuals only */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              zIndex: 1,
              borderRadius: '5px', // Apply border-radius to this container
              overflow: 'hidden',   // ADDED: This is crucial to clip the inner segments
                                   // so they respect the parent's border-radius.
            }}
          >
            {/* Prep segment */}
            {prepDays > 0 && (
              <div
              className="md-timeline-template-time"
                style={{
                  width: `${prepPercent}%`,
                  backgroundColor: prepFill,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: `1px solid ${segmentRuleColor}`,
                  boxSizing: 'border-box',
                }}
              >
                Prep
              </div>
            )}

            {/* Opptak segment */}
            {opptakDays > 0 && (
              <div
                style={{
                  width: `${opptakPercent}%`,
                  backgroundColor: opptakFill,
                  borderRight: returDays > 0 ? `1px solid ${segmentRuleColor}` : "none",
                  boxSizing: 'border-box',
                }}
              />
            )}

            {/* Retur segment */}
            {returDays > 0 && (
              <div
              className="md-timeline-template-time"
                style={{
                  width: `${returPercent}%`,
                  backgroundColor: returFill,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Retur
              </div>
            )}
          </div>
        </div>
      );
    }

    // Fallback for other event types
    // Safe date conversion for start and end
    const startDate = safeToDate(ev.start);
    const endDate = safeToDate(ev.end);
    const TotalDays = countDaysInclusiveLocal(startDate, endDate);

    // --- YOUR OTHER EVENT TYPES (STYLES RESTORED) ---
    if (ev.title === "Juleferie") {
      const lights = Math.round(TotalDays * 1.2);
      return (
        <div
          className={`christmas md-timeline-template-event md-timeline-template-event-christemas ${
            mobileShow ? "" : "mobile-toggle"
          }`}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title-christemas">
                PRIVAT: {ev.title} - {TotalDays} days
              </span>
              <span className="md-timeline-template-desc-christemas">
                {ev.description}
              </span>
            </div>
          </div>
          <ul className="strand">
            {Array.from(Array(lights), (e, i) => (
              <li key={i}></li>
            ))}
          </ul>
        </div>
      );
    }

    if (isVacationEvent(ev)) {
      return (
        <div
          className={`md-timeline-template-event md-timeline-template-event-vacation ${
            mobileShow ? "" : "mobile-toggle"
          }`}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title-vacation">
                Ferie: {ev.title} - {TotalDays} days
              </span>
              <span className="md-timeline-template-desc-vacation">
                {ev.description}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (ev.busy === true || isSickEvent(ev) || isSickChildEvent(ev)) {
      const sick = isSickEvent(ev);
      const sickChild = isSickChildEvent(ev);
      const busyTitlePrefix = sick ? "Syk: " : sickChild ? "Syke barn: " : "Opptatt: ";
      return (
        <div
          className={`md-timeline-template-event md-timeline-template-event-busy ${
            mobileShow ? "" : "mobile-toggle"
          }`}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title-busy">
                {busyTitlePrefix}
                {ev.title} - {TotalDays} days
              </span>
              <span className="md-timeline-template-desc-busy">
                {ev.description}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (!ev.editable) {
      return (
        <div
          className={`md-timeline-template-event disabled-event ${
            mobileShow ? "" : "mobile-toggle"
          }`}
          style={{
            borderColor: `${color}${Math.floor(1 * 255)
              .toString(16)
              .padStart(2, "0")}`,
            background: `${color}${Math.floor(0.4 * 255)
              .toString(16)
              .padStart(2, "0")}`,
          }}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title">
                {ev.title} - {TotalDays} days{" "}
              </span>
              <span className="md-timeline-template-desc">
                {ev.description}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Fallback for other standard events
    return (
      <div
        className={`md-timeline-template-event ${
          mobileShow ? "" : "mobile-toggle"
        }`}
        style={{
          borderColor: `${color}${Math.floor(1 * 255)
            .toString(16)
            .padStart(2, "0")}`,
          background: `${color}${Math.floor(0.4 * 255)
            .toString(16)
            .padStart(2, "0")}`,
        }}
      >
        <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
          <div id="flex-event-expand">
            <span className="md-timeline-template-title">
              {ev.title} - {TotalDays} days
            </span>
            <span className="md-timeline-template-desc">{ev.description}</span>
          </div>
        </div>
      </div>
    );
  };
};
